import React, { useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import * as THREE from 'three';

interface ScreenPropProps {
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  isSelected?: boolean;
  streamData?: MediaStream | null;
  screenType: 'screen-share' | 'browser' | 'video';
  browserUrl?: string;
  videoUrl?: string;
}

export default function ScreenProp({ position, scale, rotation, isSelected, streamData, screenType, videoUrl }: ScreenPropProps) {
  const [texture, setTexture] = useState<THREE.VideoTexture | null>(null);
  const [status, setStatus] = useState('Waiting for a screen share');
  useEffect(() => {
    setTexture(null);
    setStatus(screenType === 'video' ? 'Choose a video' : 'Waiting for a screen share');
    if (!(screenType === 'screen-share' && streamData) && !(screenType === 'video' && videoUrl)) return;
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    if (screenType === 'video') { video.crossOrigin = 'anonymous'; video.loop = true; video.src = videoUrl!; }
    else video.srcObject = streamData!;
    const tex = new THREE.VideoTexture(video);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    let disposed = false;
    const playing = () => { if (!disposed) { setTexture(tex); setStatus(''); } };
    const failed = () => { if (!disposed) { setTexture(null); setStatus('Unable to play this video'); } };
    const ended = () => { if (!disposed) { setTexture(null); setStatus('Screen sharing ended'); } };
    video.addEventListener('playing', playing);
    video.addEventListener('error', failed);
    const tracks = streamData?.getVideoTracks() || [];
    tracks.forEach(track => track.addEventListener('ended', ended));
    setStatus('Connecting…');
    void video.play().catch(failed);
    return () => {
      disposed = true;
      tracks.forEach(track => track.removeEventListener('ended', ended));
      video.removeEventListener('playing', playing);
      video.removeEventListener('error', failed);
      video.pause(); video.srcObject = null; video.removeAttribute('src'); video.load(); tex.dispose();
    };
  }, [streamData, screenType, videoUrl]);
  return <group position={position} rotation={rotation} scale={scale}>
    <mesh position={[0, 0, -0.08]}><boxGeometry args={[4.16, 2.41, 0.12]} /><meshStandardMaterial color={isSelected ? '#00a99d' : '#242b32'} /></mesh>
    <mesh><planeGeometry args={[4, 2.25]} /><meshBasicMaterial key={texture?.uuid || 'placeholder'} map={texture} color={texture ? '#ffffff' : '#141b24'} side={THREE.DoubleSide} toneMapped={false} /></mesh>
    {status && <Text position={[0, 0, 0.02]} fontSize={0.13} maxWidth={3.5} textAlign="center" color="#dce5ef">{status}</Text>}
  </group>;
}
